import { useState, useEffect } from "react";
import { images } from "../constants/images";
import "../assets/stylesheets/bootstrap.min.css";
import "../assets/stylesheets/style.css";
import "../assets/stylesheets/responsive.css";
import axios from "axios";
import cogoToast from "cogo-toast";
import { CONTACT } from "../api/utils";
import ContactStyles from "./styles/Contact";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    projectType: "",
    budget: "",
    timeline: "",
    message: "",
    howHeard: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    document.title = "Contact Us - Get Custom Software Quote | Plural Code";
    window.scrollTo(0, 0);
  }, []);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.projectType) {
      newErrors.projectType = "Please select a project type";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Please tell us about your project";
    } else if (formData.message.trim().length < 20) {
      newErrors.message =
        "Please provide more details (at least 20 characters)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to get input border color
  const getInputBorderColor = (fieldName) => {
    if (errors[fieldName]) {
      return "#dc3545"; // Red for error
    }
    if (formData[fieldName] && !errors[fieldName]) {
      return "#28a745"; // Green for valid
    }
    return "#e2e8f0"; // Default
  };

  // List of required fields
  const requiredFields = ["name", "email", "phone", "projectType", "message"];

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Prepare data for API
    const data = {
      name: formData.name,
      email: formData.email,
      subject: `New Contact Enquiry from ${formData.name} - ${formData.projectType}`,
      content: formData.message,
      source: "Plural Code Technologies Website",
      phone_number: formData.phone,
      project_type: formData.projectType,
      company_name: formData.company || "Not Available",
      estimated_budget: formData.budget || "Not Available",
      project_timeline: formData.timeline || "Not Available",
      how_did_you_hear: formData.howHeard || "Not Available",
    };

    try {
      // Make API call
      const response = await axios.post(CONTACT, data);

      if (response.data?.status === "ok") {
        // Success
        setSubmitSuccess(true);

        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          projectType: "",
          budget: "",
          timeline: "",
          message: "",
          howHeard: "",
        });

        // Show success toast
        cogoToast.success(
          response.data?.message ||
            "Your contact enquiry has been received. We will get in touch with you soon.",
        );

        // Reset success message after 5 seconds
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        // API returned error
        cogoToast.error(
          response.data?.error || "Something went wrong. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);

      // Handle different error scenarios
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        cogoToast.error(
          error.response.data?.error || "Server error. Please try again later.",
        );
      } else if (error.request) {
        // The request was made but no response was received
        cogoToast.error("Network error. Please check your connection.");
      } else {
        // Something happened in setting up the request
        cogoToast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="contact-page">
      <ContactStyles />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon">👋</span>
              <span className="badge-text">LET'S CONNECT</span>
            </div>
            <h1 className="hero-title">
              Let's Build Something{" "}
              <span className="hero-highlight">Great Together</span>
            </h1>
            <p className="hero-subtitle">
              Have a custom software project in mind? Let's discuss your
              challenges, goals, and how we can help you build a competitive
              advantage.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="contact-methods">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-4 col-md-6">
              <div className="method-card">
                <span className="method-icon">📧</span>
                <h3 className="method-title">Email Us</h3>
                <p className="method-description">
                  Send us your project details and requirements
                </p>
                <a href="mailto:info@pluralcode.com" className="method-link">
                  info@pluralcode.com →
                </a>
                <p
                  style={{
                    color: "#0A2458",
                    fontSize: "0.875rem",
                    marginTop: "8px",
                  }}
                >
                  We respond within 24 hours
                </p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6">
              <div className="method-card">
                <span className="method-icon">📞</span>
                <h3 className="method-title">Call Us</h3>
                <p className="method-description">
                  Speak with our team directly for immediate assistance
                </p>
                <a href="tel:+919958920231" className="method-link">
                  +91 99589 20231 →
                </a>
                <p
                  style={{
                    color: "#0A2458",
                    fontSize: "0.875rem",
                    marginTop: "8px",
                  }}
                >
                  Mon-Fri: 9 AM - 6 PM IST
                </p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6">
              <div className="method-card">
                <span className="method-icon">💬</span>
                <h3 className="method-title">WhatsApp</h3>
                <p className="method-description">
                  Quick chat for urgent queries and quick questions
                </p>
                <a
                  href="https://wa.me/919958920231"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-btn"
                  style={{ marginTop: "8px" }}
                >
                  Message on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="main-content">
        <div className="container">
          <div className="row g-5">
            {/* Form Column */}
            <div className="col-lg-8">
              <div className="form-section">
                <h2 className="section-title">Tell Us About Your Project</h2>
                <p className="section-subtitle">
                  Fill out the form below and we'll get back to you within 24
                  hours with next steps. All consultations are free and
                  confidential.
                </p>

                {submitSuccess && (
                  <div className="success-message">
                    <span style={{ fontSize: "24px" }}>✅</span>
                    <div>
                      <strong>Thank you!</strong> Your message has been sent
                      successfully. We'll get back to you within 24 hours.
                    </div>
                  </div>
                )}

                {/* <form onSubmit={handleSubmit}> */}
                <form>
                  <div className="row">
                    {/* Name */}
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">
                          Your Name <span style={{ color: "#dc3545" }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          className={`form-control ${
                            errors.name ? "is-invalid" : ""
                          }`}
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={handleChange}
                        />
                        {errors.name && (
                          <div className="invalid-feedback">{errors.name}</div>
                        )}
                      </div>
                    </div>

                    {/* Email */}
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">
                          Email Address{" "}
                          <span style={{ color: "#dc3545" }}>*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          className={`form-control ${
                            errors.email ? "is-invalid" : ""
                          }`}
                          placeholder="john@company.com"
                          value={formData.email}
                          onChange={handleChange}
                        />
                        {errors.email && (
                          <div className="invalid-feedback">{errors.email}</div>
                        )}
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">
                          Phone Number{" "}
                          <span style={{ color: "#dc3545" }}>*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          className={`form-control ${
                            errors.phone ? "is-invalid" : ""
                          }`}
                          placeholder="+1 234 567 8900"
                          value={formData.phone}
                          onChange={handleChange}
                        />
                        {errors.phone && (
                          <div className="invalid-feedback">{errors.phone}</div>
                        )}
                      </div>
                    </div>

                    {/* Company */}
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Company Name</label>
                        <input
                          type="text"
                          name="company"
                          className="form-control"
                          placeholder="Your Company"
                          value={formData.company}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    {/* Project Type */}
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">
                          What are you looking for?{" "}
                          <span style={{ color: "#dc3545" }}>*</span>
                        </label>
                        <select
                          name="projectType"
                          className={`form-control ${
                            errors.projectType ? "is-invalid" : ""
                          }`}
                          value={formData.projectType}
                          onChange={handleChange}
                        >
                          <option value="">Select project type...</option>
                          <option value="custom-business-software">
                            Custom Business Software
                          </option>
                          <option value="web-application">
                            Web Application
                          </option>
                          <option value="mobile-app">
                            Mobile App Development
                          </option>
                          <option value="system-integration">
                            System Integration
                          </option>
                          <option value="support-maintenance">
                            Ongoing Support & Maintenance
                          </option>
                          <option value="consultation">
                            Just Need Consultation
                          </option>
                          <option value="other">Other / Not Sure</option>
                        </select>
                        {errors.projectType && (
                          <div className="invalid-feedback">
                            {errors.projectType}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Budget */}
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Estimated Budget</label>
                        <select
                          name="budget"
                          className="form-control"
                          value={formData.budget}
                          onChange={handleChange}
                        >
                          <option value="">Select budget range...</option>
                          <option value="under-25k">Under $25,000</option>
                          <option value="25k-50k">$25,000 - $50,000</option>
                          <option value="50k-100k">$50,000 - $100,000</option>
                          <option value="100k-plus">$100,000+</option>
                          <option value="not-sure">Not Sure Yet</option>
                        </select>
                      </div>
                    </div>

                    {/* Timeline */}
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Project Timeline</label>
                        <select
                          name="timeline"
                          className="form-control"
                          value={formData.timeline}
                          onChange={handleChange}
                        >
                          <option value="">When do you need it?</option>
                          <option value="urgent">
                            Urgent (Within 1 month)
                          </option>
                          <option value="1-3-months">1-3 months</option>
                          <option value="3-6-months">3-6 months</option>
                          <option value="6-plus-months">6+ months</option>
                          <option value="flexible">
                            Flexible / Just Exploring
                          </option>
                        </select>
                      </div>
                    </div>

                    {/* How did you hear */}
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">
                          How did you hear about us?
                        </label>
                        <select
                          name="howHeard"
                          className="form-control"
                          value={formData.howHeard}
                          onChange={handleChange}
                        >
                          <option value="">Select...</option>
                          <option value="google">Google Search</option>
                          <option value="linkedin">LinkedIn</option>
                          <option value="referral">Referral</option>
                          <option value="social-media">Social Media</option>
                          <option value="blog">Blog/Article</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    {/* Message */}
                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">
                          Tell us about your project{" "}
                          <span style={{ color: "#dc3545" }}>*</span>
                        </label>
                        <textarea
                          name="message"
                          className={`form-control ${
                            errors.message ? "is-invalid" : ""
                          }`}
                          rows="6"
                          placeholder="Please describe your project, challenges you're facing, and what you're hoping to achieve. The more details you provide, the better we can help."
                          value={formData.message}
                          onChange={handleChange}
                        />
                        {errors.message && (
                          <div className="invalid-feedback">
                            {errors.message}
                          </div>
                        )}
                        <div className="form-text">
                          Minimum 20 characters. Current:{" "}
                          {formData.message.length}
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="col-12">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="submit-btn"
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm"></span>
                            Sending...
                          </>
                        ) : (
                          <>
                            Send Message
                            <span>→</span>
                          </>
                        )}
                      </button>

                      <p
                        style={{
                          color: "#0A2458",
                          fontSize: "0.875rem",
                          marginTop: "12px",
                        }}
                      >
                        🔒 Your information is secure and will never be shared
                        with third parties.
                      </p>
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* Sidebar Column */}
            <div className="col-lg-4">
              {/* What to Expect */}
              <div className="sidebar-section">
                <h3 className="sidebar-title">What Happens Next?</h3>

                <div className="process-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>We Review Your Submission</h4>
                    <p>Within 24 hours, we'll review your project details</p>
                  </div>
                </div>

                <div className="process-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Schedule a Call</h4>
                    <p>30-minute consultation to discuss your needs</p>
                  </div>
                </div>

                <div className="process-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Get Proposal</h4>
                    <p>Detailed proposal with timeline & investment</p>
                  </div>
                </div>

                <div className="process-step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h4>Start Building</h4>
                    <p>If it's a fit, we begin discovery & development</p>
                  </div>
                </div>
              </div>

              {/* Free Resources */}
              <div className="sidebar-section">
                <h3 className="sidebar-title">Free Resources</h3>
                <p
                  style={{
                    color: "#0A2458",
                    fontSize: "0.95rem",
                    marginBottom: "16px",
                  }}
                >
                  Not ready to contact yet? Check out our free tools:
                </p>
                <ul className="resources-list">
                  <li>
                    <a href="/lm/custom-software-roi-calculator">
                      <span>📊</span>
                      <span>ROI Calculator</span>
                    </a>
                  </li>
                  <li>
                    <a href="/lm/decision-matrix">
                      <span>📋</span>
                      <span>Custom vs Off-the-Shelf Matrix</span>
                    </a>
                  </li>
                  <li>
                    <a href="/blogs">
                      <span>📝</span>
                      <span>Browse Our Blog</span>
                    </a>
                  </li>
                  <li>
                    <a href="/work">
                      <span>💼</span>
                      <span>View Case Studies</span>
                    </a>
                  </li>
                </ul>
              </div>

              {/* Office Location */}
              <div className="sidebar-section">
                <h3 className="sidebar-title">📍 Our Location</h3>
                <div style={{ marginBottom: "12px" }}>
                  <strong style={{ color: "#0a2458" }}>Headquarters:</strong>
                  <p
                    style={{
                      color: "#0A2458",
                      margin: "4px 0",
                      fontSize: "0.95rem",
                    }}
                  >
                    A1/129, Sector 17, Rohini, New Delhi 110089,
                    <br />
                    India
                  </p>
                </div>
                <div>
                  <strong style={{ color: "#0a2458" }}>Serving:</strong>
                  <p
                    style={{
                      color: "#0A2458",
                      margin: "4px 0",
                      fontSize: "0.95rem",
                    }}
                  >
                    Serving businesses of all sizes and industries around the
                    world.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="faq-title">Common Questions Before Contacting</h2>

          <div className="row justify-content-center">
            <div className="col-lg-10">
              {[
                {
                  q: "Do you charge for the initial consultation?",
                  a: "No. The first consultation is completely free. We'll discuss your project, answer questions, and determine if custom software is the right solution for you.",
                },
                {
                  q: "How long does it take to get a response?",
                  a: "We respond to all inquiries within 24 hours (usually much faster). If you need urgent assistance, call or WhatsApp us directly.",
                },
                {
                  q: "Can you work with clients in different time zones?",
                  a: "Absolutely. We regularly work with clients in US, Australia, New Zealand, Singapore, and Canada. We schedule meetings at times convenient for you.",
                },
                {
                  q: "What information should I prepare before contacting?",
                  a: "A general description of your challenge and goals is enough to start. We'll help you refine requirements during our discovery process.",
                },
                {
                  q: "Do you sign NDAs?",
                  a: "Yes, we're happy to sign NDAs before discussing confidential project details.",
                },
                {
                  q: "What's the typical project timeline?",
                  a: "Most projects range from 2-6 months depending on complexity. We provide specific timelines after understanding your requirements.",
                },
              ].map((faq, index) => (
                <div key={index} className="faq-item">
                  <h3 className="faq-question">{faq.q}</h3>
                  <p className="faq-answer">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Social Links */}
      <section className="social-section">
        <div className="container">
          <h2 className="social-title">Prefer Other Ways to Connect?</h2>

          <div className="row justify-content-center">
            <div className="col-lg-3 col-md-6 mb-4">
              <div className="social-card">
                <span className="social-icon">💼</span>
                <h4 className="social-name">LinkedIn</h4>
                <p className="social-description">
                  Connect professionally with our team
                </p>
                <a
                  href="https://linkedin.com/company/plural-code"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-btn"
                >
                  Follow Us
                </a>
              </div>
            </div>

            <div className="col-lg-3 col-md-6 mb-4">
              <div className="social-card">
                <span className="social-icon">📅</span>
                <h4 className="social-name">Schedule Direct</h4>
                <p className="social-description">
                  Book a time that works for you
                </p>
                <a
                  href="https://topmate.io/pluralcode"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-btn"
                >
                  Book Meeting
                </a>
              </div>
            </div>

            <div className="col-lg-3 col-md-6 mb-4">
              <div className="social-card">
                <span className="social-icon">𝕏</span>
                <h4 className="social-name">Twitter/X</h4>
                <p className="social-description">
                  Follow for industry insights and updates
                </p>
                <a
                  href="https://twitter.com/pluralcode"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-btn"
                >
                  Follow Us
                </a>
              </div>
            </div>

            <div className="col-lg-3 col-md-6 mb-4">
              <div className="social-card">
                <span className="social-icon">📝</span>
                <h4 className="social-name">Newsletter</h4>
                <p className="social-description">
                  Get weekly insights and tips
                </p>
                <a href="/blog#newsletter" className="social-btn">
                  Subscribe
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="testimonial-section">
        <div className="container">
          <div className="testimonial-content">
            <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
            <blockquote className="testimonial-quote">
              "From the first contact to final delivery, Plural Code was
              professional, transparent, and genuinely invested in our success.
              They didn't just build software they understood our business and
              created something that truly works for us."
            </blockquote>
            <p className="testimonial-author">
              Operations Manager, Australian Manufacturing Company
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
